import { useState, useRef, useCallback, useEffect } from 'react';
import { generateAIImage, removeBackground } from '../../../ai/ImageAI';
import { generate3D, AIResult } from '../../../ai/3DAI';
import { Object } from './useArtHistory';
import example from './example.json';
import { ArtType } from '../Art';

export type Phases3D = 'image' | '3d';

interface UseAIProps {
  config: any;
  artType: ArtType;
  panelSize: number;
  userPng: string;
  description: string;
  objects: Object[];
  aiImage: string;
  setAiImage: (url: string) => void;
  setIsGeneratingImage: (isGenerating: boolean) => void;
  setArtToastMessage: (message: any) => void;
  addHistoryEntry: (entry: any) => void;
  updateEntry: (index: number, updates: any) => void;
  currentIndex: number;
  history: any[];
  aiToggle: boolean;
  isUpdatingDescription: boolean;
}

export const useAI = ({
  config,
  artType,
  panelSize,
  userPng,
  description,
  objects,
  aiImage,
  setAiImage,
  setIsGeneratingImage,
  setArtToastMessage,
  addHistoryEntry,
  updateEntry,
  currentIndex,
  history,
  aiToggle,
  isUpdatingDescription
}: UseAIProps) => {
  // Refs for tracking state between renders
  const lastRequestTimeRef = useRef(Date.now());
  const pendingRequestRef = useRef<boolean>(false);
  const currentIterationRef = useRef(0);
  const lastCompletedIterationRef = useRef(0);
  const prevImageRef = useRef('');
  const prevDescriptionRef = useRef('');
  const historyUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [model3D, setModel3D] = useState<string>('');
  const [isGenerating3D, setIsGenerating3D] = useState(false);
  const [stagePhase, setStagePhase] = useState<Phases3D>('image');
  const [button3dPhase, setButton3dPhase] = useState<Phases3D>('image');

  useEffect(() => {
    if (model3D) {
      setStagePhase('3d');
    }
  }, [model3D]);

  useEffect(() => {
      if (aiImage !== '' && aiImage !== null) {
      // if (isGenerating3D || model3D?.url) {
          setButton3dPhase('3d');
      } else {
          setButton3dPhase('image');
      }
  }, [isGenerating3D, model3D, aiImage]);

  /**
   * Generate an AI image based on text and image input
   */
  const generateImage = useCallback((text: string, image: string) => {
    // console.log('attempting to generate image: ', image, 'description: ', text);
    const iterationCount = currentIterationRef.current;
    setIsGeneratingImage(true);

    if (iterationCount > lastCompletedIterationRef.current) {
      lastCompletedIterationRef.current = iterationCount;

      if (config.DEBUG_DISABLE_IMAGE_AI) {
        // Create a mock response that mimics the real API response
        return new Promise((resolve) => {
          // Simulate a short delay to mimic network request
          setTimeout(() => {
            const response = { url: aiImage === example.image ? example.image2 : example.image };
            // console.log('Created image from iteration', iterationCount);
            lastCompletedIterationRef.current = iterationCount;
            setAiImage(response.url);

            // Check if current history entry already has an AI image
            if (history[currentIndex]?.aiImage) {
              // console.log('Creating new history entry for AI image with objects length:', objects.length);
              // Create new history entry with current state plus new AI image
              addHistoryEntry({
                objects: objects,
                userPng: userPng,
                description: description,
                aiImage: response.url
              });
            } else {
              // console.log('Updating current history entry with AI image with objects length:', objects.length);
              // Update the current history entry with the new AI image
              updateEntry(currentIndex, {
                aiImage: response.url,
              });
            }
            setIsGeneratingImage(false);
            resolve(response);
          }, 1000); // Small delay to simulate API call
        });
      }
      
      const containsImage = objects.some(obj => obj.type === 'image');
      // console.log('containsImage', containsImage);

      return generateAIImage({
        req: {
          text: text,
          image: image,
          // image: image === '' ? example.blankImage: image,
          image_size: panelSize,
          image_creativity: containsImage ? 0.5 : 0.8
        },
        config: config,
        artType: artType,
      }).then(response => {
        // console.log('Created image from iteration', iterationCount);
        setAiImage(response.url);
        
        // Check if current history entry already has an AI image
        if (history[currentIndex]?.aiImage) {
          // console.log('Creating new history entry for AI image with objects length:', objects.length);
          // Create new history entry with current state plus new AI image
          addHistoryEntry({
            objects: objects,
            userPng: userPng,
            description: description,
            aiImage: response.url
          });
        } else {
          // console.log('Updating current history entry with AI image with objects length:', objects.length);
          // Update the current history entry with the new AI image
          updateEntry(currentIndex, {
            aiImage: response.url,
          });
        }
        setIsGeneratingImage(false);
        return response;
      }).catch(error => {
        console.error('Image generation failed for iteration', iterationCount, ':', error);
        setArtToastMessage('image generation error');
        setIsGeneratingImage(false);
        throw error;
      });
    }
  }, [panelSize, config, updateEntry, currentIndex, history, objects, userPng, description, addHistoryEntry, aiImage, setAiImage, setIsGeneratingImage, setArtToastMessage]);

  /**
   * Handle background removal for an image
   */
  const handleRemoveBackground = useCallback((image: string) => {
    // console.log('attempting to remove background from image: ', image);
    
    if (config.DEBUG_DISABLE_IMAGE_AI) return;
    
    setIsGeneratingImage(true);
    return removeBackground({
      req: {
        image: image,
      },
      config: config,
    }).then(async response => {
      // console.log('Background removed successfully');
      await setAiImage(response.url);
      
      historyUpdateTimeoutRef.current = setTimeout(() => {
        const newHistory = addHistoryEntry({
          objects,
          userPng,
          description,
          aiImage: response.url,
        });
        // console.log('added new history entry in remove bg: ', newHistory);
      }, 500);
      setIsGeneratingImage(false);
      return response;
    }).catch(error => {
      console.error('Background removal failed:', error);
      throw error;
    });
  }, [config, addHistoryEntry, objects, userPng, description, setAiImage, setIsGeneratingImage]);

  /**
   * Throttled image generation with various checks to prevent unnecessary API calls
   */
  const throttledGenerateImage = useCallback(async (force: boolean = false) => {
    const currentTime = Date.now();
    const timeSinceLastRequest = currentTime - lastRequestTimeRef.current;

    if (!aiToggle) {
      // console.log('Skipping generation - AI is ', aiToggle);
      return;
    }

    // Skip if the user hasn't drawn anything yet
    if (userPng === '') {
      // console.log('No image to generate, skipping');
      // return;
    }

    // Skip if there's no description
    if (description === '' && !force) {
      // console.log('No description to generate, skipping');
      return;
    }
    
    // Skip if there's a pending request
    if (pendingRequestRef.current) {
      // console.log('Skipping generation - request already pending');
      return;
    }

    // Skip if user is actively typing description
    if (!force && isUpdatingDescription) {
      // console.log('Skipping generation - user is typing');
      return;
    }

    // Check if inputs have changed
    if (!force && userPng === prevImageRef.current && description === prevDescriptionRef.current) {
      // console.log('Skipping generation - no changes detected');
      return;
    }

    // Enforce minimum time between requests unless forced
    if (!force && timeSinceLastRequest < config.AUTO_GENERATE_IMAGE_DELAY) {
      // console.log('Throttling - too soon since last request');
      return;
    }

    // console.log('Starting image generation');
    pendingRequestRef.current = true;
    lastRequestTimeRef.current = currentTime;
    prevImageRef.current = userPng;
    prevDescriptionRef.current = description;
    
    // Increment iteration before starting new request
    currentIterationRef.current += 1;
    const currentIteration = currentIterationRef.current;
    // console.log('Starting iteration', currentIteration);

    try {
      // console.log('Calling generateImage with: ', description, userPng);
      await generateImage(description, userPng);
    } catch (error) {
      console.error('Generation failed for iteration', currentIteration, ':', error);
    } finally {
      pendingRequestRef.current = false;
    }
  }, [userPng, description, generateImage, config.AUTO_GENERATE_IMAGE_DELAY, isUpdatingDescription, aiToggle]);

  /**
   * Handle 3D model generation from the current AI image
   */
  const handle3DGeneration = async () => {
    // console.log('3D clicked');
    
    try {
      setIsGenerating3D(true);
      setStagePhase('3d');
      const result = await generate3D({
        config,
        imageUrl: aiImage,
        setIsGenerating: setIsGenerating3D,
        onResult: (result: AIResult) => {
          setModel3D(result.url);
          // console.log('3D result', result);
          addHistoryEntry({
            objects,
            userPng,
            description,
            aiImage,
            ai3dModel: result.url,
          });
        }
      });
      return result;
    } catch (error) {
      console.error('3D generation failed:', error);
      setIsGenerating3D(false);
    }
  };

  return {
    generateImage,
    handleRemoveBackground,
    throttledGenerateImage,
    handle3DGeneration,
    model3D,
    setModel3D,
    isGenerating3D,
    stagePhase,
    setStagePhase,
    button3dPhase,
    setButton3dPhase
  };
};
